import {
  type UserRoleType,
  useGetDocument,
  useGetDocumentsUserRoles,
  usePutDocumentUserRoles,
} from 'entities/document/api';
import { useTranslation } from 'react-i18next';
import { useShowNotification } from '../useShowNotification';

/**
 * Type definition for the properties of the DocumentManagers hook.
 */
export type DocumentManagersProps = {
  documentId?: number;
};

/**
 * Hook to manage document managers
 * @param props - DocumentManagersProps
 */
export const useDocumentManagers = (props: DocumentManagersProps) => {
  const { t } = useTranslation('documents');

  /**
   * Custom hook to show notifications.
   */
  const showNotification = useShowNotification();

  /**
   * Hook to fetch a specific document by its ID.
   */
  const { data: document } = useGetDocument(props.documentId);

  /**
   * Hook to fetch managers related to the document, if specified.
   * This is only called if `loadManagers` is true.
   */
  const { data: managersRaw, ...restProps } = useGetDocumentsUserRoles(
    props.documentId,
  );

  /**
   * Hook to update managers for the document.
   */
  const { mutateAsync: managersMutation } = usePutDocumentUserRoles(
    props.documentId,
  );

  /**
   * Sets the managers for a document.
   * @param event - The event object.
   * @param emails - Array of email addresses to set as managers.
   */
  const setManagers = async (
    event: React.SyntheticEvent | undefined,
    emails: string[],
  ) => {
    if (!props.documentId) {
      return;
    }
    event?.stopPropagation();
    await managersMutation(emails);
    showNotification({
      message: t('documents:notification.managersUpdated'),
    });
  };

  /**
   * Update the manager's list with the role of the author if the author is a manager.
   */
  const managers: UserRoleType[] =
    managersRaw?.map(manager => ({
      ...manager,
      role: document?.author.id === manager.user.id ? 'author' : manager.role,
    })) || [];

  return {
    /**
     * Managers related to the document, if available.
     */
    managers,

    /**
     * Actions related to the document managers.
     */
    actions: {
      /**
       * Sets the managers for the document.
       */
      setManagers,
    },

    /**
     * Other props from the hook.
     */
    ...restProps,
  };
};
